import z from "zod";

import { apiClient, endPoint } from "@/lib/client";
import {
  AcademicCategorySchema,
  BusinessSchema,
  GenderSchema,
  JobSchema,
  NotificationGroupSchema,
  PrefectureSchema,
  SchoolSchema,
  UserProfileSchema,
  UserRoleTypeSchema,
  UserSchema,
  type UserProfileType,
  type UserType,
  prefectureLabelMap
} from "@/types/account";
import { Zodios, makeApi, makeErrors } from "@zodios/core";

export type UserCreateRequestType = z.infer<typeof UserCreateRequestSchema>;
export type UserProfileUpdateRequestType = z.infer<typeof UserProfileUpdateRequestSchema>;
export type UserProfileCreateRequestType = z.infer<typeof UserProfileCreateRequestSchema>;
export type UserAdditionalInfoCreateRequestType = z.infer<
  typeof UserAdditionalInfoCreateRequestSchema
>;
export type UserAdditionalInfoUpdateRequestType = z.infer<
  typeof UserAdditionalInfoUpdateRequestSchema
>;
export type SchoolSearchResponseType = z.infer<typeof SchoolSearchResponseSchema>;
export type SchoolSearchRequestType = z.infer<typeof SchoolSearchRequestSchema>;
export type UpsertNotificationSettingsRequestType = z.infer<
  typeof UpsertNotificationSettingsRequestSchema
>;
export type AddressResultType = z.infer<typeof AddressResultSchema>;
export type ZipCloudResponseType = z.infer<typeof ZipCloudResponseSchema>;

export const UserProfileUpdateRequestSchema = z
  .object({
    sex: z.string(),
    school_type: SchoolSchema,
    school_name: z.string(),
    academic_category: AcademicCategorySchema,
    major: z.string(),
    graduate_year: z.number(),
    phone_number: z.string(),
    postal_code: z.string(),
    prefecture: PrefectureSchema,
    city: z.string(),
    address1: z.string(),
    address2: z.string(),
    self_pr: z.string(),
    interesting_jobs: z.string().array(),
    interesting_businesses: z.string().array()
  })
  .partial();

export const UserProfileCreateRequestSchema = z
  .object({
    sex: GenderSchema,
    school_type: SchoolSchema,
    school_name: z.string(),
    academic_category: AcademicCategorySchema,
    major: z.string(),
    graduate_year: z.number(),
    phone_number: z.string(),
    postal_code: z.string(),
    prefecture: PrefectureSchema,
    city: z.string(),
    address1: z.string(),
    address2: z.string(),
    self_pr: z.string(),
    interesting_jobs: JobSchema.array(),
    interesting_businesses: BusinessSchema.array()
  })
  .partial();

export const UserAdditionalInfoCreateRequestSchema = z.object({
  uuid: z.string().uuid(),
  ip_address: z.string().ip(),
  network: z.string(),
  ip_version: z.string()
});

export const UserAdditionalInfoUpdateRequestSchema = z.object({
  uuid: z.string().uuid(),
  user_id: z.string(),
  ip_address: z.string().ip(),
  network: z.string(),
  ip_version: z.string()
});

export const AddressResultSchema = z.union([
  z.object({
    status: z.union([z.literal("idle"), z.literal("progress")])
  }),
  z.object({
    status: z.literal("done"),
    prefecture: PrefectureSchema,
    city: z.string()
  })
]);

export const ZipCloudResponseSchema = z.object({
  message: z.string().nullable(),
  results: z
    .array(
      z.object({
        address1: z.string(),
        address2: z.string(),
        address3: z.string()
      })
    )
    .nullable(),
  status: z.number()
});

export const UserCreateRequestSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  icon: z.string().url(),
  role: UserRoleTypeSchema
});

export const SchoolSearchResponseSchema = z.object({
  school_code: z.string(),
  school_name: z.string(),
  school_type_code: z.string(),
  school_type: z.string()
});

export const SchoolSearchRequestSchema = z.object({
  school_type: z.string(),
  keyword: z.string()
});

export const UpsertNotificationSettingsRequestSchema = z.object({
  unsubscribe_group_ids: z.string(),
  subscribe_group_ids: z.string()
});

const errors = makeErrors([
  {
    status: 404,
    description: "User not found",
    schema: z.object({
      error: z.object({
        userId: z.number(),
        code: z.string(),
        message: z.string()
      })
    })
  },
  {
    status: 401,
    description: "Unauthorized",
    schema: z.object({
      code: z.string(),
      message: z.string()
    })
  },
  {
    status: 500,
    description: "Profile not set yet",
    schema: z
      .object({
        code: z.string(),
        message: z.string()
      })
      .passthrough()
  },
  {
    status: "default",
    description: "Default error",
    schema: z.object({
      error: z.object({
        code: z.string(),
        message: z.string()
      })
    })
  }
]);

export const accountApi = makeApi([
  {
    alias: "getProfile",
    method: "get",
    path: "/user_profile",
    response: UserProfileSchema,
    errors
  },
  {
    alias: "createProfile",
    method: "post",
    path: "/user_profile",
    response: UserProfileSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserProfileCreateRequestSchema
      }
    ]
  },
  {
    alias: "updateProfile",
    method: "patch",
    path: "/user_profile",
    response: UserProfileSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserProfileUpdateRequestSchema
      }
    ]
  },
  {
    alias: "createUser",
    method: "post",
    path: "/user",
    response: UserSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserCreateRequestSchema
      }
    ],
    errors
  },
  {
    alias: "initializeGuest",
    method: "post",
    path: "/user/initialize",
    response: UserSchema,
    errors
  },
  {
    alias: "_getMe",
    method: "get",
    path: "/user/me",
    response: UserSchema,
    errors
  },
  {
    alias: "getUser",
    method: "get",
    path: "/user/:id",
    response: UserSchema,
    errors
  },
  {
    alias: "getAllUsers",
    method: "get",
    path: "/user",
    response: UserSchema.array(),
    errors
  },
  {
    alias: "getNotificationSettings",
    method: "get",
    path: "/notification/my_setting",
    response: NotificationGroupSchema.array()
  },
  {
    alias: "upsertNotificationSettings",
    method: "post",
    path: "/notification/my_setting",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpsertNotificationSettingsRequestSchema
      }
    ],
    response: z.null()
  },
  {
    alias: "getSchool",
    method: "get",
    path: "/schools",
    response: SchoolSearchResponseSchema
  },
  {
    alias: "searchSchools",
    method: "get",
    path: "/schools/search",
    response: SchoolSearchResponseSchema.array(),
    parameters: [
      {
        name: "school_type",
        type: "Query",
        schema: z.string()
      },
      {
        name: "keyword",
        type: "Query",
        schema: z.string()
      }
    ]
  }
]);

export const _fetchMe = async (cookie?: string | undefined): Promise<UserType> => {
  if (cookie) {
    return await apiClient._getMe({ headers: { cookie } });
  }
  return await apiClient._getMe();
};

export const _fetchProfile = async (cookie?: string | undefined): Promise<UserProfileType> => {
  const response = await fetch(`${endPoint}/user_profile`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(cookie ? { cookie } : {})
    },
    credentials: "include",
    mode: "cors"
  });

  if (!response.ok) {
    const error: any = new Error(`Failed to fetch profile: ${response.statusText}`);
    error.response = { status: response.status };
    throw error;
  }

  const data = await response.json();
  return UserProfileSchema.parse(data);
};

export async function initializeGuest() {
  return await fetch(`${endPoint}/user/initialize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    mode: "cors"
  });
}

const zipCodeSchema = z.string().regex(/^\d{7}$/, "郵便番号は7桁の数字である必要があります");

export const getAddress = async (
  zipCode: z.infer<typeof zipCodeSchema>
): Promise<AddressResultType> => {
  try {
    const apiClient = new Zodios("https://zipcloud.ibsnet.co.jp/api", [
      {
        alias: "getAddressByZipCloud",
        method: "get",
        path: "/search",
        response: ZipCloudResponseSchema,
        parameters: [
          {
            name: "zipcode",
            type: "Query",
            schema: zipCodeSchema
          }
        ]
      }
    ]);

    const response = await apiClient.getAddressByZipCloud({
      queries: {
        zipcode: zipCode
      }
    });

    if (response.results && response.results.length > 0) {
      const result = response.results[0];
      const prefectureEntry = Object.entries(prefectureLabelMap).find(
        ([, value]) => value === result.address1
      );

      if (!prefectureEntry) {
        return { status: "idle" };
      }

      const [prefectureCode] = prefectureEntry;
      return {
        status: "done",
        prefecture: PrefectureSchema.parse(prefectureCode),
        city: `${result.address2}${result.address3}`
      };
    }

    return { status: "idle" };
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error("バリデーションエラー:", error.errors);
    } else {
      console.error("郵便番号検索でエラーが発生しました:", error);
    }
    return { status: "idle" };
  }
};
